/**
 * Product Form Component
 * MPAD Implementation: Phase 1 - Progressive Disclosure Product Management
 * 
 * Implements Maya's progressive disclosure pattern to manage complexity
 * while providing comprehensive product catalog functionality.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Button,
  Collapse,
  Typography,
  Alert,
  Chip,
  Grid,
  InputAdornment,
  Tooltip,
  IconButton,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  Info,
  Save,
  Cancel,
  Inventory,
  Scale,
  LocalShipping,
  Science
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

// ===============================================
// TYPES AND INTERFACES
// ===============================================

interface Product {
  id?: number;
  name: string;
  category: string;
  unit_type: 'weight' | 'volume' | 'count' | 'pallets' | 'mixed';
  weight_per_unit_kg?: number;
  volume_per_unit_m3?: number;
  length_cm?: number;
  width_cm?: number;
  height_cm?: number;
  stackable: boolean;
  requires_refrigeration: boolean;
  hazmat_class?: string;
  handling_instructions?: string;
}

interface ProductFormProps {
  initialData?: Partial<Product>;
  onSave: (product: Product) => Promise<void>;
  onCancel: () => void;
  mode?: 'create' | 'edit' | 'duplicate';
  loading?: boolean;
}

type FormStage = 'basic' | 'physical' | 'advanced';

// ===============================================
// VALIDATION SCHEMA
// ===============================================

const productValidationSchema = z.object({
  name: z.string()
    .min(1, 'Product name is required')
    .max(255, 'Name must be less than 255 characters')
    .regex(/^[a-zA-Z0-9\s\-_.]+$/, 'Name can only contain letters, numbers, spaces, hyphens, underscores, and dots'),
  
  category: z.string()
    .max(100, 'Category must be less than 100 characters')
    .optional(),
  
  unit_type: z.enum(['weight', 'volume', 'count', 'pallets', 'mixed'], {
    errorMap: () => ({ message: 'Invalid unit type' })
  }),
  
  weight_per_unit_kg: z.number()
    .min(0, 'Weight must be positive')
    .max(1000000, 'Weight must be less than 1,000,000 kg')
    .nullable()
    .optional(),
  
  volume_per_unit_m3: z.number()
    .min(0, 'Volume must be positive')
    .max(10000, 'Volume must be less than 10,000 m³')
    .nullable()
    .optional(),
  
  length_cm: z.number()
    .min(0, 'Length must be positive')
    .max(10000, 'Length must be less than 10,000 cm')
    .nullable()
    .optional(),
  
  width_cm: z.number()
    .min(0, 'Width must be positive')
    .max(10000, 'Width must be less than 10,000 cm')
    .nullable()
    .optional(),
  
  height_cm: z.number()
    .min(0, 'Height must be positive')
    .max(10000, 'Height must be less than 10,000 cm')
    .nullable()
    .optional(),
  
  stackable: z.boolean(),
  
  requires_refrigeration: z.boolean(),
  
  hazmat_class: z.string()
    .regex(/^[1-9](\.[1-9])?$/, 'Invalid hazmat class (format: 1-9 or 1.1-9.9)')
    .nullable()
    .optional(),
  
  handling_instructions: z.string()
    .max(1000, 'Handling instructions must be less than 1000 characters')
    .nullable()
    .optional()
}).refine((data) => {
  // Custom validation for weight_per_unit_kg when unit_type is 'weight'
  if (data.unit_type === 'weight' && !data.weight_per_unit_kg) {
    return false;
  }
  return true;
}, {
  message: 'Weight per unit is required for weight-based products',
  path: ['weight_per_unit_kg']
}).refine((data) => {
  // Custom validation for volume_per_unit_m3 when unit_type is 'volume'
  if (data.unit_type === 'volume' && !data.volume_per_unit_m3) {
    return false;
  }
  return true;
}, {
  message: 'Volume per unit is required for volume-based products',
  path: ['volume_per_unit_m3']
}).refine((data) => {
  // Custom validation for mixed unit type
  if (data.unit_type === 'mixed' && (!data.weight_per_unit_kg || !data.volume_per_unit_m3)) {
    return false;
  }
  return true;
}, {
  message: 'Both weight and volume are required for mixed dimension products',
  path: ['weight_per_unit_kg']
});

// ===============================================
// CONSTANTS
// ===============================================

const PRODUCT_CATEGORIES = [
  'General',
  'Packages',
  'Bulk',
  'Refrigerated',
  'Hazardous',
  'Fragile',
  'Express',
  'Documents',
  'Electronics',
  'Food & Beverage',
  'Pharmaceuticals',
  'Automotive',
  'Construction',
  'Textiles'
];

const UNIT_TYPE_INFO = {
  weight: {
    label: 'Weight-based',
    description: 'Capacity calculated by weight (kg)',
    icon: <Scale />,
    fields: ['weight_per_unit_kg']
  },
  volume: {
    label: 'Volume-based',
    description: 'Capacity calculated by volume (m³)',
    icon: <Inventory />,
    fields: ['volume_per_unit_m3']
  },
  count: {
    label: 'Count-based',
    description: 'Capacity calculated by item count',
    icon: <LocalShipping />,
    fields: []
  },
  pallets: {
    label: 'Pallet-based',
    description: 'Capacity calculated by pallet spaces',
    icon: <LocalShipping />,
    fields: []
  },
  mixed: {
    label: 'Mixed dimensions',
    description: 'Multiple capacity dimensions',
    icon: <Science />,
    fields: ['weight_per_unit_kg', 'volume_per_unit_m3']
  }
};

// ===============================================
// MAIN COMPONENT
// ===============================================

const ProductForm: React.FC<ProductFormProps> = ({
  initialData,
  onSave,
  onCancel,
  mode = 'create',
  loading = false
}) => {
  const [formStage, setFormStage] = useState<FormStage>('basic');
  const [estimatedTime, setEstimatedTime] = useState('30 seconds');

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isValid, isDirty },
    setValue,
    trigger
  } = useForm<Product>({
    resolver: zodResolver(productValidationSchema),
    defaultValues: {
      name: '',
      category: 'General',
      unit_type: 'weight',
      stackable: true,
      requires_refrigeration: false,
      ...initialData
    },
    mode: 'onChange'
  });

  const watchedUnitType = watch('unit_type');
  const watchedName = watch('name');

  // Update estimated completion time based on form stage
  useEffect(() => {
    switch (formStage) {
      case 'basic':
        setEstimatedTime('30 seconds');
        break;
      case 'physical':
        setEstimatedTime('1 minute');
        break;
      case 'advanced':
        setEstimatedTime('2 minutes');
        break;
    }
  }, [formStage]);

  // Auto-advance to physical stage when unit type requires it
  useEffect(() => {
    if (watchedUnitType && ['weight', 'volume', 'mixed'].includes(watchedUnitType) && formStage === 'basic') {
      setFormStage('physical');
    }
  }, [watchedUnitType, formStage]);

  const handleFormSubmit = async (data: Product) => {
    try {
      await onSave(data);
    } catch (error) {
      console.error('Error saving product:', error);
    }
  };

  const getStageTitle = (stage: FormStage) => {
    switch (stage) {
      case 'basic':
        return 'Basic Information';
      case 'physical':
        return 'Physical Properties';
      case 'advanced':
        return 'Advanced Options';
      default:
        return '';
    }
  };

  const isStageComplete = (stage: FormStage) => {
    switch (stage) {
      case 'basic':
        return watchedName && watchedName.length > 0;
      case 'physical':
        if (watchedUnitType === 'weight') {
          return watch('weight_per_unit_kg') !== undefined;
        }
        if (watchedUnitType === 'volume') {
          return watch('volume_per_unit_m3') !== undefined;
        }
        if (watchedUnitType === 'mixed') {
          return watch('weight_per_unit_kg') !== undefined && watch('volume_per_unit_m3') !== undefined;
        }
        return true;
      case 'advanced':
        return true;
      default:
        return false;
    }
  };

  return (
    <Card sx={{ maxWidth: 800, mx: 'auto' }}>
      <CardHeader
        title={
          <Box display="flex" alignItems="center" gap={1}>
            <Inventory />
            <Typography variant="h5">
              {mode === 'create' ? 'Create Product' : 
               mode === 'edit' ? 'Edit Product' : 'Duplicate Product'}
            </Typography>
          </Box>
        }
        subheader={
          <Box display="flex" alignItems="center" gap={2} mt={1}>
            <Chip 
              label={getStageTitle(formStage)} 
              color="primary" 
              size="small" 
            />
            <Typography variant="body2" color="text.secondary">
              Estimated time: {estimatedTime}
            </Typography>
          </Box>
        }
      />

      <CardContent>
        <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
          {/* Stage 1: Basic Information */}
          <Box mb={3}>
            <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
              <Chip 
                label="1" 
                size="small" 
                color={isStageComplete('basic') ? 'success' : 'default'} 
              />
              Basic Information
              {isStageComplete('basic') && (
                <Chip label="Complete" size="small" color="success" variant="outlined" />
              )}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Product Name"
                      fullWidth
                      required
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      placeholder="e.g., Standard Box, Pallet Load, Document Envelope"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Inventory color="action" />
                          </InputAdornment>
                        )
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name="category"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Category</InputLabel>
                      <Select {...field} label="Category">
                        {PRODUCT_CATEGORIES.map((category) => (
                          <MenuItem key={category} value={category}>
                            {category}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="unit_type"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth required>
                      <InputLabel>Unit Type</InputLabel>
                      <Select {...field} label="Unit Type">
                        {Object.entries(UNIT_TYPE_INFO).map(([key, info]) => (
                          <MenuItem key={key} value={key}>
                            <Box display="flex" alignItems="center" gap={1}>
                              {info.icon}
                              <Box>
                                <Typography variant="body1">{info.label}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {info.description}
                                </Typography>
                              </Box>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Stage 2: Physical Properties */}
          {(['weight', 'volume', 'mixed'].includes(watchedUnitType)) && (
            <Box mb={3}>
              <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                <Chip 
                  label="2" 
                  size="small" 
                  color={isStageComplete('physical') ? 'success' : 'default'} 
                />
                Physical Properties
                {isStageComplete('physical') && (
                  <Chip label="Complete" size="small" color="success" variant="outlined" />
                )}
                <Tooltip title="Required for capacity calculations">
                  <IconButton size="small">
                    <Info />
                  </IconButton>
                </Tooltip>
              </Typography>

              <Alert severity="info" sx={{ mb: 2 }}>
                Physical properties are used to automatically calculate capacity requirements.
                This replaces meaningless numbers with real-world measurements.
              </Alert>

              <Grid container spacing={2}>
                {(watchedUnitType === 'weight' || watchedUnitType === 'mixed') && (
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="weight_per_unit_kg"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          value={field.value || ''}
                          onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                          label="Weight per Unit"
                          type="number"
                          fullWidth
                          required={watchedUnitType === 'weight'}
                          error={!!errors.weight_per_unit_kg}
                          helperText={errors.weight_per_unit_kg?.message}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">kg</InputAdornment>,
                            startAdornment: (
                              <InputAdornment position="start">
                                <Scale color="action" />
                              </InputAdornment>
                            )
                          }}
                          inputProps={{
                            min: 0,
                            step: 0.001
                          }}
                        />
                      )}
                    />
                  </Grid>
                )}

                {(watchedUnitType === 'volume' || watchedUnitType === 'mixed') && (
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="volume_per_unit_m3"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          value={field.value || ''}
                          onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                          label="Volume per Unit"
                          type="number"
                          fullWidth
                          required={watchedUnitType === 'volume'}
                          error={!!errors.volume_per_unit_m3}
                          helperText={errors.volume_per_unit_m3?.message}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">m³</InputAdornment>,
                            startAdornment: (
                              <InputAdornment position="start">
                                <Inventory color="action" />
                              </InputAdornment>
                            )
                          }}
                          inputProps={{
                            min: 0,
                            step: 0.0001
                          }}
                        />
                      )}
                    />
                  </Grid>
                )}

                {/* Dimensions */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Dimensions (Optional)
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Controller
                    name="length_cm"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        value={field.value || ''}
                        onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                        label="Length"
                        type="number"
                        fullWidth
                        error={!!errors.length_cm}
                        helperText={errors.length_cm?.message}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">cm</InputAdornment>
                        }}
                        inputProps={{
                          min: 0,
                          step: 0.1
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Controller
                    name="width_cm"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        value={field.value || ''}
                        onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                        label="Width"
                        type="number"
                        fullWidth
                        error={!!errors.width_cm}
                        helperText={errors.width_cm?.message}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">cm</InputAdornment>
                        }}
                        inputProps={{
                          min: 0,
                          step: 0.1
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Controller
                    name="height_cm"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        value={field.value || ''}
                        onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                        label="Height"
                        type="number"
                        fullWidth
                        error={!!errors.height_cm}
                        helperText={errors.height_cm?.message}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">cm</InputAdornment>
                        }}
                        inputProps={{
                          min: 0,
                          step: 0.1
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Stage 3: Advanced Options */}
          <Box mb={3}>
            <Button
              onClick={() => setFormStage(formStage === 'advanced' ? 'physical' : 'advanced')}
              startIcon={formStage === 'advanced' ? <ExpandLess /> : <ExpandMore />}
              sx={{ mb: 2 }}
            >
              <Chip 
                label="3" 
                size="small" 
                color="default"
                sx={{ mr: 1 }}
              />
              Advanced Options
            </Button>

            <Collapse in={formStage === 'advanced'}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="stackable"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            {...field}
                            checked={field.value}
                          />
                        }
                        label="Stackable"
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="requires_refrigeration"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            {...field}
                            checked={field.value}
                          />
                        }
                        label="Requires Refrigeration"
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="hazmat_class"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        value={field.value || ''}
                        label="Hazmat Classification"
                        fullWidth
                        error={!!errors.hazmat_class}
                        helperText={errors.hazmat_class?.message || 'UN hazmat classification (e.g., 1.1, 3, 9)'}
                        placeholder="e.g., 1.1, 3, 9"
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="handling_instructions"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        value={field.value || ''}
                        label="Handling Instructions"
                        multiline
                        rows={3}
                        fullWidth
                        error={!!errors.handling_instructions}
                        helperText={errors.handling_instructions?.message}
                        placeholder="Special handling requirements, storage conditions, safety notes..."
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Collapse>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Form Actions */}
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button
              variant="outlined"
              onClick={onCancel}
              startIcon={<Cancel />}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} /> : <Save />}
              disabled={!isValid || loading}
            >
              {loading ? 'Saving...' : 'Save Product'}
            </Button>
          </Box>
        </form>
      </CardContent>
    </Card>
  );
};

export default ProductForm;